import * as borogove from "borogove";
import Delta from "quill-delta";
import { href as rhref } from "react-router";
import { useRef, useCallback, useEffect } from "react";

export const href = (
	template: string,
	params: Record<string, string>,
	query?: URLSearchParams,
) => {
	const escapedParams: Record<string, string> = {};
	for (const [k, v] of Object.entries(params)) {
		escapedParams[k] = encodeURIComponent(v).replace(/%40/, "@");
	}
	const url = rhref(template, escapedParams);
	return url + (query?.size ? "?" + query : "");
};

export function useEffectEvent<Args extends unknown[], R>(
	fn: (...args: Args) => R,
): (...args: Args) => R {
	const ref = useRef(fn);

	// Keep the latest function in ref
	useEffect(() => {
		ref.current = fn;
	});

	// Return a stable function that always calls the latest version
	return useCallback((...args: Args) => {
		return ref.current(...args);
	}, []);
}

export type Attachment = { attachment: borogove.ChatAttachment; uri: string };
export type Draft = {
	text?: string;
	delta?: Delta;
	attachments?: Attachment[];
	currentThread: string | null;
	scrollTo?: string | null;
};

// We never show this while it is changing so it doesn't need to be reactive
export const useDrafts = () => {
	const drafts = useRef<Record<string, Draft | null>>({});

	return {
		getDraft: (id: string) => drafts.current[id],
		setDraft: (id: string, draft: Draft | null) => (drafts.current[id] = draft),
	};
};

export const showNotification = async (
	accountId: string,
	message: borogove.ChatMessage,
) => {
	if (!Notification?.permission) return;

	const tag = accountId + ">" + message.chatId();
	if (navigator.serviceWorker) {
		// Check if we already notified this message
		const serviceWorkerRegistration = await navigator.serviceWorker.ready;
		const notifs = await serviceWorkerRegistration.getNotifications({
			tag: tag,
		});
		for (const notif of notifs) {
			if (notif.data.messageId === (message.serverId || message.localId))
				return;
		}
	}

	const title = "Message from " + message.chatId();
	const options = {
		body: message.text ?? undefined,
		tag: tag,
		timestamp: new Date(message.timestamp).getTime(),
		vibrate: [50, 50, 50],
		data: {
			accountId,
			chatId: message.chatId(),
			messageId: message.serverId || message.localId,
			timestamp: message.timestamp,
		},
	};
	if (navigator.serviceWorker) {
		const serviceWorkerRegistration = await navigator.serviceWorker.ready;
		await serviceWorkerRegistration.showNotification(title, options);
	} else {
		const notif = new Notification(title, options);
		notif.addEventListener("click", () => {
			history.pushState(
				null,
				"",
				href("/accounts/:accountId/chats/:chatId", {
					accountId: accountId,
					chatId: message.chatId(),
				}) + `#message:${message.serverId || message.localId}`,
			);
			window.dispatchEvent(new PopStateEvent("popstate")); // Notify React Router
		});
		return notif;
	}
};
